import { ReactNode, useCallback } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Link, Typography } from "@mui/material";
import { EmptyStateIcon } from "./empty-state-icon";
import { ComplianceFramework } from "~/operations";
import { EmptyExceptionsIcon } from "./empty-exceptions-icon";
import { docsHref } from "~/lib/docs-href";
import { DataObject as DataObjectIcon } from "@mui/icons-material";
import { ExceptionTarget } from "~/components/exceptions/types";
import { renderAssetEmptyStateContent } from "~/pages/inventory/asset/utils";
import { organizationSpacesEmptyStateContent } from "~/pages/organization/utils/organizationSpacesEmptyState";
import EmptyAddButton from "~/images/empty-add-button.png";
import { organizationSpacesFilteredEmptyStateContent } from "~/pages/organization/utils/organizationSpacesFilteredEmptyState";
import { ClipboardPassIcon, FleetIcon, VulnerabilitiesIcon } from "~/icons";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";

type Props = {
  scope?: SpaceScope | OrganizationScope;
  frameworkMrn?: ComplianceFramework["mrn"];
};

type ActionButton = {
  to: string;
  text: string;
};

type EmptyStateContent = {
  icon: ReactNode;
  headline: string;
  textContent?: ReactNode;
  actionButtons?: ActionButton[];
};

export function useEmptyState({ scope, frameworkMrn }: Props) {
  const emptyStateContent = useCallback(
    (contentType: string) => {
      return getContent(contentType);
    },
    [frameworkMrn, scope],
  );

  function renderExceptionsEmptyStateContent({
    target,
  }: {
    target: ExceptionTarget | "framework";
  }): EmptyStateContent {
    const targetLabelMap: Record<typeof target, string> = {
      check: "these checks",
      control: "these controls",
      framework: "this framework",
      asset: "this asset",
      advisory: "this advisory",
      cve: "this CVE",
      finding: "this finding",
      policy: "this policy",
    };

    return {
      icon: <EmptyExceptionsIcon sx={{ fontSize: 38 }} />,
      headline: `There are no exceptions to ${targetLabelMap[target]}.`,
      textContent: (
        <>
          <Typography color="text.secondary" sx={{ textAlign: "center" }}>
            Mondoo lets you disable and accept the risk for{" "}
            {target === "framework"
              ? "controls in a framework"
              : targetLabelMap[target]}
            .
          </Typography>
          <Typography color="text.secondary" sx={{ textAlign: "center" }}>
            To learn about these exceptions and how to set them, read the{" "}
            <Link
              href={docsHref("/platform/compliance/customize/exceptions/")}
              target="_blank"
              rel="noopener"
            >
              Mondoo documentation
            </Link>
            .
          </Typography>
        </>
      ),
    };
  }

  const getContent = (contentType: string): EmptyStateContent => {
    switch (contentType) {
      case "security-policy-assets":
      case "control-assets":
      case "framework-assets":
        return {
          icon: <EmptyStateIcon sx={{ fontSize: 38 }} />,
          headline: "There are no assets to show yet.",
          textContent: (
            <>
              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                After you add integrations, assets will appear here.
              </Typography>
            </>
          ),
          actionButtons: scope && [
            {
              text: "Add Integration",
              to: `/space/integrations/add?${scope.params}`,
            },
          ],
        };

      case "data-query-assets":
        return {
          icon: <EmptyStateIcon sx={{ fontSize: 38 }} />,
          headline: "There are no assets to show yet.",
          textContent: (
            <>
              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                After you add integrations, assets will appear here.
              </Typography>
            </>
          ),
          actionButtons: scope && [
            {
              text: "Add Integration",
              to: `/space/integrations/add?${scope.params}`,
            },
          ],
        };

      case "control-checks":
        const frameworkId = useParams<{ frameworkId: string }>().frameworkId;
        return {
          icon: <EmptyStateIcon sx={{ fontSize: 38 }} />,
          headline: "There are no checks for this control.",
          textContent: (
            <>
              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                Checks exist in policies. Enable one or more recommended
                policies
              </Typography>
              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                to see checks for this control.
              </Typography>
            </>
          ),
          // if space, frameworkId, frameworkMrn exists, show actionButton
          ...(scope &&
            frameworkId &&
            frameworkMrn && {
              actionButtons: [
                {
                  text: "View Policies",
                  to: `/space/compliance/${frameworkId}/policies?${scope.params}&frameworkMrn=${frameworkMrn}`,
                },
              ],
            }),
        };

      case "asset-checks-tab":
        return renderAssetEmptyStateContent({ target: "checks", scope });

      case "asset-policies-tab":
        return renderAssetEmptyStateContent({
          target: "policies",
          scope,
        });

      case "control-exceptions":
        return renderExceptionsEmptyStateContent({ target: "control" });

      case "check-exceptions":
        return renderExceptionsEmptyStateContent({ target: "check" });

      case "framework-exceptions":
        return renderExceptionsEmptyStateContent({ target: "framework" });

      case "data-queries":
        return {
          icon: <DataObjectIcon sx={{ fontSize: 38, color: "primary.main" }} />,
          headline: "There are no data queries to show.",
          textContent: (
            <>
              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                Don't worry; not all controls include data queries.
              </Typography>
            </>
          ),
          actionButtons: scope && [
            {
              text: "Add Query Packs",
              to: `/space/inventory/query-packs/add?${scope.params}`,
            },
          ],
        };

      case "policy-assets":
        return {
          icon: <FleetIcon sx={{ fontSize: 38, color: "primary.main" }} />,
          headline: `There are 0 assets this check applies to.`,
          textContent: (
            <Typography color="text.secondary" sx={{ textAlign: "center" }}>
              If you feel there should be something here{", "}
              <Link
                key="/space/integrations"
                component={RouterLink}
                to={`/space/integrations?${scope?.params}`}
                sx={{ textDecoration: "underline" }}
                color="text.secondary"
              >
                check your integrations
              </Link>
              .
            </Typography>
          ),
        };

      case "CVEs":
      case "advisories":
      case "exposed assets":
        return {
          icon: (
            <VulnerabilitiesIcon sx={{ fontSize: 38, color: "primary.main" }} />
          ),
          headline: `No ${contentType} found.`,
        };

      case "organization-spaces":
        return organizationSpacesEmptyStateContent();
      case "organization-spaces-filtered":
        return organizationSpacesFilteredEmptyStateContent();

      case "policies":
        return {
          icon: (
            <Box
              component="img"
              src={EmptyAddButton}
              width={380}
              alt="Add policy illustration"
            />
          ),
          headline: "Time to add your first policy!",
          textContent: (
            <Typography color="text.secondary" sx={{ textAlign: "center" }}>
              Add policies that match your infrastructure to your space, and run
              them in preview or scored mode
              <br /> to improve your security posture and benchmark your
              successes
            </Typography>
          ),
        };
      case "query-packs":
        return {
          icon: (
            <Box
              component="img"
              src={EmptyAddButton}
              width={380}
              alt="Add query pack illustration"
            />
          ),
          headline: "Time to add your first query pack!",
          textContent: (
            <Typography color="text.secondary" sx={{ textAlign: "center" }}>
              Add query packs that match your infrastructure to your space,
              <br /> and gain unprecedented insight into your infrastructure's
              metadata
            </Typography>
          ),
        };

      case "integrations-finalize":
        return {
          icon: <ClipboardPassIcon sx={{ fontSize: 38, color: "none.main" }} />,
          headline: "All recommended policies are enabled!",
          textContent: (
            <>
              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                Browse our policies and query packs to find more content that
                matches your security goals and infrastructure
              </Typography>
            </>
          ),
          actionButtons: scope && [
            {
              text: "Browse Policies",
              to: `/space/findings/policies/add?${scope.params}`,
            },
            {
              text: "Browse Query Packs",
              to: `/space/inventory/query-packs/add?${scope.params}`,
            },
          ],
        };

      case "detected-assets":
        return {
          icon: <EmptyStateIcon sx={{ fontSize: 38 }} />,
          headline: "No detected assets found.",
        };

      default:
        return {
          icon: <EmptyStateIcon sx={{ fontSize: 38 }} />,
          headline: "No results found.",
        };
    }
  };

  return { emptyStateContent };
}
